/**
 * Add Company Page
 * Full-page form for registering a new company with all required sections
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Upload,
  message,
  Space,
  Card,
  Divider,
  Alert,
} from 'antd';
import {
  ApartmentOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  BarChartOutlined,
  FileTextOutlined,
  UploadOutlined,
  GlobalOutlined,
  UserOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import type { UploadFile } from 'antd/es/upload/interface';
import SuperAdminSidebar from '../components/superadmin/SuperAdminSidebar';
import SuperAdminHeader from '../components/superadmin/SuperAdminHeader';
import superAdminService, { CompanyCreate, LicenseUploadResponse } from '../services/superAdminService';
import { useResponsive } from '../hooks/useResponsive';
import { prepareDocumentForUpload, formatFileKb, LOGO_COMPRESS_OPTIONS } from '../utils/compressDocument';
import './AddCompany.css';

const { TextArea } = Input;
const { Option } = Select;
const { Content } = Layout;

function mapDzongkhag(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const key = raw.toLowerCase().replace(/[^a-z]/g, '');
  const aliases: Record<string, string> = {
    thimphu: 'thimphu',
    paro: 'paro',
    punakha: 'punakha',
    wangdue: 'wangdue',
    wangduephodrang: 'wangdue',
    bumthang: 'bumthang',
    trongsa: 'trongsa',
    zhemgang: 'zhemgang',
    mongar: 'mongar',
    lhuentse: 'lhuentse',
    trashigang: 'trashigang',
    trashiyangtse: 'trashiyangtse',
    trashiyangtsi: 'trashiyangtse',
    pemagatshel: 'pemagatshel',
    samdrupjongkhar: 'samdrup',
    samdrup: 'samdrup',
    sarpang: 'sarpang',
    tsirang: 'tsirang',
    dagana: 'dagana',
    chhukha: 'chhukha',
    chukha: 'chhukha',
    haa: 'haa',
    samtse: 'samtse',
  };
  return aliases[key];
}

function applyLicenseSuggestions(form: FormInstance, response: LicenseUploadResponse): string[] {
  const suggestions = response.suggestions || {};
  const extracted = response.extracted_data || {};
  const patch: Record<string, any> = {};
  const setVal = (field: string, value?: string | null) => {
    if (value && String(value).trim()) patch[field] = String(value).trim();
  };
  setVal('name', suggestions.company_name || extracted.name);
  setVal('registration_number', suggestions.registration_number || extracted.registration_number);
  setVal('tax_id', suggestions.tax_id || extracted.tax_id);
  setVal('address', suggestions.address || extracted.address);
  setVal('phone', suggestions.phone || extracted.phone);
  setVal('email', suggestions.email || extracted.email);
  setVal('license_type', suggestions.license_type || extracted.license_type);
  setVal('gewog', suggestions.gewog);
  setVal('thromde', suggestions.thromde || suggestions.village || suggestions.exact_location);
  const dz = mapDzongkhag(suggestions.dzongkhag);
  if (dz) patch.dzongkhag = dz;
  const ownerList = Array.isArray(suggestions.owners) ? suggestions.owners : [];
  const owners = ownerList.filter((o) => o && (o.name || o.cid_number));
  const companyPatch = { ...patch };
  if (owners.length === 1) {
    companyPatch.ownership_type = 'sole_proprietorship';
  } else if (owners.length > 1) {
    companyPatch.ownership_type = 'partnership';
  } else if (suggestions.ownership_type) {
    companyPatch.ownership_type = suggestions.ownership_type;
  }
  form.setFieldsValue(companyPatch);
  window.setTimeout(() => {
    if (owners.length === 1) {
      form.setFieldsValue({
        ownership_type: 'sole_proprietorship',
        owner_name: owners[0].name || '',
        owner_cid_number: owners[0].cid_number || '',
      });
    } else if (owners.length > 1) {
      form.setFieldsValue({
        ownership_type: 'partnership',
        owners: owners.map((o) => ({
          name: o.name || '',
          email: o.email || '',
          phone: o.phone || '',
          cid_number: o.cid_number || '',
        })),
      });
    }
  }, 50);
  return Object.keys(companyPatch);
}

function getPasswordScore(pwd: string): number {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (/[A-Z]/.test(pwd)) score += 1;
  if (/[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
  return score;
}

function getPasswordStrengthLabel(score: number): string {
  if (score <= 1) return 'Weak';
  if (score <= 3) return 'Fair';
  if (score === 4) return 'Good';
  return 'Strong';
}

const AddCompany: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  // File states
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [licenseFileList, setLicenseFileList] = useState<UploadFile[]>([]);
  const [cidFile, setCidFile] = useState<File | null>(null);
  const [cidFileList, setCidFileList] = useState<UploadFile[]>([]);
  const [ownershipFile, setOwnershipFile] = useState<File | null>(null);
  const [ownershipFileList, setOwnershipFileList] = useState<UploadFile[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFileList, setLogoFileList] = useState<UploadFile[]>([]);
  const [ocrSummary, setOcrSummary] = useState<string | null>(null);
  
  
  // Watch ownership_type from form to show/hide owner details
  const ownershipType = Form.useWatch('ownership_type', form);
  const adminPassword = Form.useWatch('admin_password', form) || '';
  const adminPasswordScore = useMemo(() => getPasswordScore(String(adminPassword)), [adminPassword]);
  const adminPasswordStrength = useMemo(() => getPasswordStrengthLabel(adminPasswordScore), [adminPasswordScore]);
  const selectedPlanType = String(Form.useWatch('plan_type', form) || 'basic').toLowerCase();
  const selectedSubscriptionPeriod = String(Form.useWatch('subscription_period', form) || 'yearly').toLowerCase();
  const selectedMaxUsers = Number(Form.useWatch('max_users', form) || 2);
  const selectedMaxLocations = Number(Form.useWatch('max_locations', form) || 1);
  
  // Check if single owner (sole proprietorship) or multiple owners
  const isSingleOwner = ownershipType === 'sole_proprietorship';

  const handleLicenseUpload = async (file: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type) && !/\.(pdf|jpe?g|png|gif|webp)$/i.test(file.name)) {
      message.error('License file must be PDF, JPG, PNG, GIF, or WEBP');
      return false;
    }

    const hideCompress = message.loading('Compressing license if needed...', 0);
    let fileToUse: File;
    try {
      fileToUse = await prepareDocumentForUpload(file);
      hideCompress();
      if (fileToUse.size < file.size) {
        message.success(`License compressed (${formatFileKb(file.size)} → ${formatFileKb(fileToUse.size)})`);
      }
    } catch (err: any) {
      hideCompress();
      message.error(err?.message || 'Could not compress license');
      return false;
    }
    setLicenseFile(fileToUse);
    setLicenseFileList([{ uid: '-1', name: fileToUse.name, status: 'uploading' }]);
    try {
      const response = await superAdminService.uploadLicense(fileToUse);
      applyLicenseSuggestions(form, response);
      setLicenseFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
      setOcrSummary(response.message || 'License attached. Enter company and owner details below.');
      message.success(response.message || 'License attached.');
    } catch (error: any) {
      setLicenseFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
      setOcrSummary('License selected. Enter company and owner details below.');
      message.warning(error?.message || 'License selected. Enter details manually.');
    }
    return false;
  };

  const handleCidUpload = async (file: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      message.error('CID file must be PDF, JPG, PNG, GIF, or WEBP');
      return false;
    }

    const hideCompress = message.loading('Compressing CID if needed...', 0);
    let fileToUse: File;
    try {
      fileToUse = await prepareDocumentForUpload(file);
      hideCompress();
      if (fileToUse.size < file.size) {
        message.success(`CID compressed (${formatFileKb(file.size)} → ${formatFileKb(fileToUse.size)})`);
      }
    } catch (err: any) {
      hideCompress();
      message.error(err?.message || 'Could not compress CID');
      return false;
    }
    setCidFile(fileToUse);
    setCidFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
    message.success('CID file selected. Please fill in the admin name manually.');
    return false; // file is sent with the form submit, not uploaded separately
  };

  const handleOwnershipUpload = async (file: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      message.error('Ownership document must be PDF, JPG, PNG, GIF, or WEBP');
      return false;
    }

    const hideCompress = message.loading('Compressing document if needed...', 0);
    let fileToUse: File;
    try {
      fileToUse = await prepareDocumentForUpload(file);
      hideCompress();
      if (fileToUse.size < file.size) {
        message.success(`Document compressed (${formatFileKb(file.size)} → ${formatFileKb(fileToUse.size)})`);
      }
    } catch (err: any) {
      hideCompress();
      message.error(err?.message || 'Could not compress document');
      return false;
    }
    setOwnershipFile(fileToUse);
    setOwnershipFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
    return false;
  };

  const handleLogoUpload = async (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      message.error('Logo must be an image (JPG, PNG, GIF, or WEBP)');
      return false;
    }

    const hide = message.loading('Compressing logo if needed...', 0);
    try {
      const fileToUse = await prepareDocumentForUpload(file, LOGO_COMPRESS_OPTIONS);
      hide();
      if (fileToUse.size < file.size) {
        message.success(`Logo compressed (${formatFileKb(file.size)} → ${formatFileKb(fileToUse.size)})`);
      }
      setLogoFile(fileToUse);
      setLogoFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
    } catch (err: any) {
      hide();
      message.error(err?.message || 'Could not compress logo');
      return false;
    }
    return false;
  };



  const handleOwnershipTypeChange = (value: string) => {
    if (!value) {
      form.setFieldsValue({
        owner_name: '',
        owner_email: '',
        owner_phone: '',
        owner_cid_number: '',
        owners: [],
      });
      return;
    }
    if (value === 'sole_proprietorship') {
      return;
    }
    const current = form.getFieldValue('owners');
    const hasOwner = Array.isArray(current) && current.some((o: any) => o && (o.name || o.cid_number || o.email || o.phone));
    if (!hasOwner) {
      form.setFieldsValue({ owners: [{ name: '', email: '', phone: '', cid_number: '' }] });
    }
  };

  const handleSubmit = async (values: any) => {
    if (!cidFile) {
      message.error('Please upload Admin CID Copy');
      return;
    }
    if (!licenseFile) {
      message.error('Please upload License Copy');
      return;
    }

    // Validate owner CID numbers
    if (ownershipType) {
      if (isSingleOwner) {
        if (!values.owner_cid_number || !values.owner_cid_number.trim()) {
          message.error('Please enter Owner CID Number');
          return;
        }
      } else {
        const owners = values.owners || [];
        if (owners.length === 0) {
          message.error('Please add at least one owner');
          return;
        }
        // Check if all owners have CID numbers
        for (let i = 0; i < owners.length; i++) {
          if (!owners[i].cid_number || !owners[i].cid_number.trim()) {
            message.error(`Please enter CID Number for Owner ${i + 1}`);
            return;
          }
        }
      }
    }

    // Ownership document is optional — no validation required

    setLoading(true);
    try {
      const normalizedPlan = String(
        values.plan_type ?? form.getFieldValue('plan_type') ?? selectedPlanType ?? 'basic'
      )
        .trim()
        .toLowerCase();
      const plan_type =
        normalizedPlan === 'enterprise'
          ? 'enterprise'
          : normalizedPlan === 'premium' || normalizedPlan === 'standard'
            ? 'premium'
            : 'basic';

      const companyData: CompanyCreate = {
        name: values.name,
        description: values.description,
        email: values.email,
        phone: values.phone,
        address: values.address,
        country: values.country || 'Bhutan',
        dzongkhag: values.dzongkhag,
        gewog: values.gewog,
        thromde: values.thromde,
        registration_number: values.registration_number,
        tax_id: values.tax_id,
        license_type: values.license_type,
        ownership_type: values.ownership_type,
        plan_type,
        max_users: values.max_users || 10,
        max_locations: values.max_locations || 3,
        subscription_period: values.subscription_period || 'yearly',
        // Owner data
        ...(isSingleOwner ? {
          owner_name: values.owner_name,
          owner_email: values.owner_email,
          owner_phone: values.owner_phone,
          owner_cid_number: values.owner_cid_number,
        } : {
          owners: values.owners || [],
        }),
        admin_username: values.admin_username,
        admin_email: values.admin_email,
        admin_password: values.admin_password,
        admin_name: values.admin_name,
        primary_contact: values.primary_contact,
        remarks: values.remarks,
        cid_file: cidFile,
        license_file: licenseFile || undefined,
        ownership_document: ownershipFile || undefined,
        logo_file: logoFile || undefined,
      };

      await superAdminService.createCompanyWithAdminAndCID(companyData);
      message.success('Company registered successfully!');
      navigate('/superadmin/companies');
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d: any) => d.msg || d.message).join(', ') : error.message || 'Failed to register company';
      message.error(msg);
      console.error('Error creating company:', error?.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <Layout className={`superadmin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ minHeight: '100dvh', background: '#edf1f7', display: 'flex', flexDirection: 'row', overflowX: 'hidden', maxWidth: '100vw' }}>
      <SuperAdminHeader 
        onMenuClick={handleMenuClick}
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
      />
      <SuperAdminSidebar collapsed={sidebarCollapsed} onCollapse={setSidebarCollapsed} />
      <div
        className="superadmin-content-wrapper"
        style={{
          flex: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? 80 : 280),
          marginTop: isMobile ? 64 : 80,
          transition: 'margin-left 0.3s ease, width 0.3s ease',
          width: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          maxWidth: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          minHeight: isMobile ? 'calc(100dvh - 64px)' : 'calc(100dvh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
      <Content
        className="main-content-full superadmin-content add-company-content"
        style={{
          flex: 1,
          minWidth: 0,
          margin: 0,
          background: '#edf1f7',
          padding: isMobile ? 12 : 24,
          overflowX: 'hidden',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <div className="add-company-page">
          {/* Page Header */}
          <div className="add-company-header">
            <h1 className="add-company-title">Add New Company</h1>
            <p className="add-company-subtitle">Upload the trade license first, then enter company and owner details.</p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              plan_type: 'basic',
              subscription_period: 'yearly',
              max_users: 2,
              max_locations: 1,
              country: 'Bhutan',
            }}
            autoComplete="off"
          >
            {/* Hidden field so plan_type is always part of Form submit values */}
            <Form.Item name="plan_type" hidden rules={[{ required: true, message: 'Please select plan type' }]}>
              <Input type="hidden" />
            </Form.Item>

            {/* Hidden fake inputs so browser autofill targets these instead of real fields */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
              <input type="text" name="username" tabIndex={-1} autoComplete="username" readOnly aria-hidden="true" />
              <input type="password" name="password" tabIndex={-1} autoComplete="current-password" readOnly aria-hidden="true" />
              <input type="password" name="new-password" tabIndex={-1} autoComplete="new-password" readOnly aria-hidden="true" />
            </div>
            {/* License first — file is stored; details are entered on the form */}
            <Card className="form-section-card document-upload-card">
              <div className="form-section-header">
                <FileTextOutlined className="section-icon document-icon" />
                <span className="section-title">1. Upload trade license</span>
              </div>
              <p className="document-upload-hint" style={{ marginBottom: 16, color: '#666', fontSize: 13 }}>
                Upload the Bhutan trade license (PDF or image) first, then fill company and owner details. License lookup from an official API can be added later.
              </p>
              <Form.Item
                label="License copy (PDF or image)"
                required
                tooltip="Must be uploaded first. Company and owner details are entered on this form."
              >
                <Upload
                  beforeUpload={handleLicenseUpload}
                  fileList={licenseFileList}
                  onRemove={() => {
                    setLicenseFile(null);
                    setLicenseFileList([]);
                    setOcrSummary(null);
                  }}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                >
                  <Button icon={<UploadOutlined />} type="primary">
                    Upload license
                  </Button>
                </Upload>
                <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
                  {licenseFileList.length === 0 ? 'PDF or image. Files over 2MB are compressed automatically.' : licenseFileList[0].name}
                </div>
                {ocrSummary ? (
                  <Alert style={{ marginTop: 12 }} type="success" showIcon message={ocrSummary} />
                ) : null}
              </Form.Item>
            </Card>

            {/* Company Information Section */}
            <Card className="form-section-card company-info-card">
              <div className="form-section-header">
                <ApartmentOutlined className="section-icon" />
                <span className="section-title">2. Company information</span>
              </div>
              <div className="add-company-form-grid company-info-grid">
                <Form.Item
                  className="ac-company-full-row"
                  label="Company Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter company name' }]}
                >
                  <Input autoComplete="off" placeholder="Enter company name" />
                </Form.Item>
                <Form.Item
                  className="ac-company-full-row"
                  label="License Number"
                  name="registration_number"
                  rules={[{ required: true, message: 'Please enter license number' }]}
                >
                  <Input autoComplete="off" placeholder="Enter license number" />
                </Form.Item>
                <Form.Item
                  label="Company Type"
                  name="license_type"
                  rules={[{ required: true, message: 'Please select company type' }]}
                >
                  <Select placeholder="Select type">
                    <Option value="micro">Micro</Option>
                    <Option value="macro">Macro</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Ownership Type"
                  name="ownership_type"
                  rules={[{ required: true, message: 'Please select ownership type' }]}
                >
                  <Select 
                    placeholder="Select ownership"
                    onChange={handleOwnershipTypeChange}
                  >
                    <Option value="sole_proprietorship">Sole Proprietorship</Option>
                    <Option value="partnership">Partnership</Option>
                    <Option value="private_limited">Private Limited</Option>
                    <Option value="public_limited">Public Limited</Option>
                  </Select>
                </Form.Item>
                <div className="company-info-divider" />
                <div className="company-info-contact-label">Contact Details</div>
                <Form.Item
                  label="Tax ID"
                  name="tax_id"
                >
                  <Input autoComplete="off" placeholder="Enter tax ID" />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                >
                  <Input autoComplete="off" placeholder="Enter company email" />
                </Form.Item>
                <Form.Item
                  label="Phone"
                  name="phone"
                >
                  <Input autoComplete="off" addonBefore={<span className="ac-phone-prefix">+975</span>} placeholder="XXXXXXXX" />
                </Form.Item>
                <Form.Item
                  label="Website"
                  name="website"
                >
                  <Input autoComplete="off" placeholder="Enter website URL" prefix={<GlobalOutlined />} />
                </Form.Item>
              </div>
              
              {/* Owner Details Form - Appears when ownership type is selected */}
              {ownershipType && (
                <Card className="form-section-card owner-details-card">
                  <div className="form-section-header">
                    <UserOutlined className="section-icon owner-icon" />
                    <span className="section-title">Owner Details</span>
                  </div>
                  <p style={{ margin: '0 0 16px', color: '#666', fontSize: 12 }}>
                    Names and CID numbers come from the license. Enter each owner&apos;s email and phone (those are not printed on the license).
                  </p>
                  
                  {/* Single Owner Form (Sole Proprietorship) */}
                  {isSingleOwner ? (
                    <div className="add-company-form-grid">
                      <Form.Item
                        label="Owner Name"
                        name="owner_name"
                        rules={[{ required: true, message: 'Please enter owner name' }]}
                      >
                        <Input autoComplete="off" placeholder="Enter owner name" />
                      </Form.Item>
                      <Form.Item
                        label="Owner Email"
                        name="owner_email"
                        rules={[
                          { required: true, message: 'Please enter owner email' },
                          { type: 'email', message: 'Please enter a valid email' },
                        ]}
                      >
                        <Input autoComplete="off" placeholder="Enter owner email" />
                      </Form.Item>
                      <Form.Item
                        label="Owner Phone Number"
                        name="owner_phone"
                        rules={[{ required: true, message: 'Please enter owner phone number' }]}
                      >
                        <Input autoComplete="off" addonBefore={<span className="ac-phone-prefix">+975</span>} placeholder="XXXXXXXX" />
                      </Form.Item>
                      <Form.Item
                        label="Owner CID Number"
                        name="owner_cid_number"
                        rules={[{ required: true, message: 'Please enter owner CID number' }]}
                      >
                        <Input autoComplete="off" placeholder="Enter CID number" />
                      </Form.Item>
                    </div>
                  ) : (
                    /* Multiple Owners Form (Partnership, Private Limited, Public Limited) */
                    <Form.List name="owners" initialValue={[{ name: '', email: '', phone: '', cid_number: '' }]}>
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field, index) => (
                            <Card
                              className="owner-entry-card"
                              key={field.key}
                              style={{ marginBottom: 16 }}
                            >
                              <div className="owner-entry-header">
                                <span className="owner-entry-label">
                                  <span className="owner-entry-num">{index + 1}</span>
                                  {`Owner ${index + 1}`}
                                </span>
                                {fields.length > 1 && (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() => remove(field.name)}
                                    size="small"
                                    className="owner-remove-btn"
                                  >
                                    Remove
                                  </Button>
                                )}
                              </div>
                              <div className="add-company-form-grid">
                                <Form.Item
                                  {...field}
                                  label="Owner Name"
                                  name={[field.name, 'name']}
                                  rules={[{ required: true, message: 'Please enter owner name' }]}
                                >
                                  <Input autoComplete="off" placeholder="Enter owner name" />
                                </Form.Item>
                                <Form.Item
                                  {...field}
                                  label="Owner Email"
                                  name={[field.name, 'email']}
                                  rules={[
                                    { required: true, message: 'Please enter owner email' },
                                    { type: 'email', message: 'Please enter a valid email' },
                                  ]}
                                >
                                  <Input autoComplete="off" placeholder="Enter owner email" />
                                </Form.Item>
                                <Form.Item
                                  {...field}
                                  label="Owner Phone Number"
                                  name={[field.name, 'phone']}
                                  rules={[{ required: true, message: 'Please enter owner phone number' }]}
                                >
                                  <Input autoComplete="off" addonBefore={<span className="ac-phone-prefix">+975</span>} placeholder="XXXXXXXX" />
                                </Form.Item>
                                <Form.Item
                                  {...field}
                                  label="Owner CID Number"
                                  name={[field.name, 'cid_number']}
                                  rules={[{ required: true, message: 'Please enter owner CID number' }]}
                                >
                                  <Input autoComplete="off" placeholder="Enter CID number" />
                                </Form.Item>
                              </div>
                            </Card>
                          ))}
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => add()}
                            block
                            className="owner-add-btn"
                          >
                            Add Another Owner
                          </Button>
                        </>
                      )}
                    </Form.List>
                  )}
                </Card>
              )}
            </Card>

            {/* Location Information Section */}
            <Card className="form-section-card location-info-card">
              <div className="form-section-header">
                <EnvironmentOutlined className="section-icon location-icon" />
                <span className="section-title">Location Information</span>
              </div>
              <div className="add-company-form-grid location-info-grid">
                <Form.Item
                  label="Country"
                  name="country"
                  rules={[{ required: true, message: 'Please enter country' }]}
                  initialValue="Bhutan"
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter country"
                    prefix={<GlobalOutlined />}
                    readOnly
                    suffix={<span className="ac-fixed-pill">Fixed</span>}
                  />
                </Form.Item>
                <Form.Item
                  label="Dzongkhag"
                  name="dzongkhag"
                  rules={[{ required: true, message: 'Please select dzongkhag' }]}
                >
                  <Select placeholder="Select dzongkhag">
                    <Option value="thimphu">Thimphu</Option>
                    <Option value="paro">Paro</Option>
                    <Option value="punakha">Punakha</Option>
                    <Option value="wangdue">Wangdue Phodrang</Option>
                    <Option value="bumthang">Bumthang</Option>
                    <Option value="trongsa">Trongsa</Option>
                    <Option value="zhemgang">Zhemgang</Option>
                    <Option value="mongar">Mongar</Option>
                    <Option value="lhuentse">Lhuentse</Option>
                    <Option value="trashigang">Trashigang</Option>
                    <Option value="trashiyangtse">Trashiyangtse</Option>
                    <Option value="pemagatshel">Pemagatshel</Option>
                    <Option value="samdrup">Samdrup Jongkhar</Option>
                    <Option value="sarpang">Sarpang</Option>
                    <Option value="tsirang">Tsirang</Option>
                    <Option value="dagana">Dagana</Option>
                    <Option value="chhukha">Chhukha</Option>
                    <Option value="haa">Haa</Option>
                    <Option value="samtse">Samtse</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Gewog"
                  name="gewog"
                  rules={[{ required: true, message: 'Please enter gewog' }]}
                >
                  <Input autoComplete="off" placeholder="Enter gewog" />
                </Form.Item>
                <Form.Item
                  label="Thromde"
                  name="thromde"
                >
                  <Input autoComplete="off" placeholder="Enter thromde" />
                </Form.Item>
                <div className="location-info-divider" />
                <Form.Item
                  className="ac-location-address-row"
                  label="Address"
                  name="address"
                >
                  <TextArea rows={4} placeholder="Enter full address" />
                </Form.Item>
              </div>
            </Card>

            {/* Admin Information Section */}
            <Card className="form-section-card admin-info-card">
              <div className="form-section-header">
                <SafetyOutlined className="section-icon admin-icon" />
                <span className="section-title">Admin Information</span>
              </div>
              <div className="add-company-form-grid admin-info-grid">
                <Form.Item
                  label="Admin Name"
                  name="admin_name"
                  rules={[{ required: true, message: 'Please enter admin name' }]}
                >
                  <Input autoComplete="off" placeholder="Enter admin name" />
                </Form.Item>
                <Form.Item
                  label="Admin Username"
                  name="admin_username"
                  rules={[{ required: true, message: 'Please enter username' }]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter username"
                    name="company_admin_username_field"
                    readOnly
                    onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </Form.Item>
                <Form.Item
                  label="Admin Email"
                  name="admin_email"
                  rules={[
                    { required: true, message: 'Please enter admin email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="admin@company.bt"
                    name="company_admin_email_field"
                    readOnly
                    onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                    data-lpignore="true"
                    data-form-type="other"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </Form.Item>
                <Form.Item
                  label="Create Password"
                  name="admin_password"
                  rules={[
                    { required: true, message: 'Please enter create password' },
                    { min: 8, message: 'Password must be at least 8 characters' },
                    {
                      validator: async (_, value) => {
                        const v = String(value || '');
                        if (!v) return Promise.resolve();
                        if (!/[A-Z]/.test(v)) return Promise.reject(new Error('Include at least one uppercase letter'));
                        if (!/[a-z]/.test(v)) return Promise.reject(new Error('Include at least one lowercase letter'));
                        if (!/\d/.test(v)) return Promise.reject(new Error('Include at least one number'));
                        if (!/[^A-Za-z0-9]/.test(v)) return Promise.reject(new Error('Include at least one special character'));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.Password
                    placeholder="Create password"
                    autoComplete="one-time-code"
                    name="company_admin_password_field"
                    readOnly
                    onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </Form.Item>
                <div className="password-strength-wrap ac-password-strength-row" aria-label={`Password strength: ${adminPasswordStrength}`}>
                  <div className="password-strength-bars" role="presentation">
                    {[1, 2, 3, 4].map((n) => (
                      <span
                        key={n}
                        className={`password-strength-bar ${n <= adminPasswordScore ? 'is-active' : ''} ${
                          adminPasswordScore <= 1
                            ? 'level-weak'
                            : adminPasswordScore <= 3
                              ? 'level-fair'
                              : adminPasswordScore === 4
                                ? 'level-good'
                                : 'level-strong'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="password-strength-text">{adminPassword ? adminPasswordStrength : 'Password strength'}</span>
                </div>
                <div className="admin-info-divider" />
                <Form.Item
                  className="ac-primary-contact-row"
                  label="Primary Contact"
                  name="primary_contact"
                  rules={[{ required: true, message: 'Please enter primary contact' }]}
                >
                  <Input
                    autoComplete="off"
                    addonBefore={<span className="ac-phone-prefix">+975</span>}
                    placeholder="XXXXXXXX"
                  />
                </Form.Item>
                <Form.Item
                  label="Confirm Password"
                  className="ac-password-confirm-row"
                  name="admin_password_confirm"
                  dependencies={['admin_password']}
                  rules={[
                    { required: true, message: 'Please confirm password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('admin_password') === value) return Promise.resolve();
                        return Promise.reject(new Error('The two passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Confirm password"
                    autoComplete="one-time-code"
                    name="company_admin_password_confirm_field"
                    readOnly
                    onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </Form.Item>
              </div>
            </Card>

            {/* Subscription Plan Section */}
            <Card className="form-section-card subscription-card">
              <div className="form-section-header">
                <BarChartOutlined className="section-icon subscription-icon" />
                <span className="section-title">Subscription Plan</span>
              </div>
              <div className="subscription-plan-label">
                Plan Type <span>*</span>
              </div>
              <div className="subscription-plan-cards" role="radiogroup" aria-label="Plan type">
                {[
                  {
                    key: 'basic',
                    name: 'Basic',
                    desc: 'Essential features for small teams just getting started.',
                    badge: 'Starter',
                  },
                  {
                    key: 'premium',
                    name: 'Standard',
                    desc: 'Full-featured plan for growing businesses and teams.',
                    badge: 'Most Popular',
                    popular: true,
                  },
                  {
                    key: 'enterprise',
                    name: 'Premium',
                    desc: 'Advanced capabilities with priority support and no limits.',
                    badge: 'Enterprise',
                  },
                ].map((plan) => {
                  const isSelected = selectedPlanType === plan.key;
                  return (
                    <button
                      key={plan.key}
                      type="button"
                      className={`subscription-plan-card ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => {
                        const presets: Record<string, { max_users: number; max_locations: number }> = {
                          basic: { max_users: 2, max_locations: 1 },
                          premium: { max_users: 6, max_locations: 4 },
                          enterprise: { max_users: 15, max_locations: 6 },
                        };
                        const p = presets[plan.key] || presets.basic;
                        form.setFieldsValue({ plan_type: plan.key, max_users: p.max_users, max_locations: p.max_locations });
                      }}
                    >
                      <span className="subscription-plan-card-top">
                        <span className="subscription-plan-name">{plan.name}</span>
                        <span className={`subscription-plan-check ${isSelected ? 'is-selected' : ''}`} aria-hidden>
                          {isSelected ? <CheckOutlined /> : null}
                        </span>
                      </span>
                      <span className="subscription-plan-desc">{plan.desc}</span>
                      <span className={`subscription-plan-badge ${plan.popular ? 'popular' : ''}`}>{plan.badge}</span>
                    </button>
                  );
                })}
              </div>

              <div className="add-company-form-grid subscription-grid">
                <Form.Item
                  className="subscription-period-row"
                  label="Subscription Period"
                  name="subscription_period"
                  rules={[{ required: true, message: 'Please select subscription period' }]}
                >
                  <Select placeholder="Select period">
                    <Option value="monthly">Monthly (30 days)</Option>
                    <Option value="yearly">Yearly (365 days)</Option>
                  </Select>
                </Form.Item>
                <div className="subscription-spacer" />
                <Form.Item
                  label="Max Users"
                  name="max_users"
                  rules={[{ required: true, message: 'Please enter max users' }]}
                >
                  <InputNumber min={1} controls style={{ width: '100%' }} placeholder="Enter max users" />
                </Form.Item>
                <Form.Item
                  label="Max Locations"
                  name="max_locations"
                  rules={[{ required: true, message: 'Please enter max locations' }]}
                >
                  <InputNumber min={1} controls style={{ width: '100%' }} placeholder="Enter max locations" />
                </Form.Item>
              </div>

              <div className="subscription-summary-strip" aria-label="Selected subscription summary">
                <div className="subscription-summary-item">
                  <span className="subscription-summary-label">Plan</span>
                  <span className="subscription-summary-value">
                    {selectedPlanType === 'basic' ? 'Basic' : selectedPlanType === 'premium' ? 'Standard' : 'Premium'}
                  </span>
                </div>
                <div className="subscription-summary-divider" />
                <div className="subscription-summary-item">
                  <span className="subscription-summary-label">Period</span>
                  <span className="subscription-summary-value">
                    {selectedSubscriptionPeriod === 'monthly' ? 'Monthly · 30 days' : 'Yearly · 365 days'}
                  </span>
                </div>
                <div className="subscription-summary-divider" />
                <div className="subscription-summary-item">
                  <span className="subscription-summary-label">Max Users</span>
                  <span className="subscription-summary-value">{selectedMaxUsers}</span>
                </div>
                <div className="subscription-summary-divider" />
                <div className="subscription-summary-item">
                  <span className="subscription-summary-label">Max Locations</span>
                  <span className="subscription-summary-value">{selectedMaxLocations}</span>
                </div>
              </div>
            </Card>

            {/* Remaining documents (license is uploaded at the top) */}
            <Card className="form-section-card document-upload-card">
              <div className="form-section-header">
                <FileTextOutlined className="section-icon document-icon" />
                <span className="section-title">Other documents</span>
              </div>
              <p className="document-upload-hint" style={{ marginBottom: 16, color: '#666', fontSize: 12 }}>
                Admin CID is still required. Ownership letter and logo are optional.
              </p>
              <Form.Item
                label="Admin CID Copy"
                required
              >
                <Upload
                  beforeUpload={handleCidUpload}
                  fileList={cidFileList}
                  onRemove={() => {
                    setCidFile(null);
                    setCidFileList([]);
                  }}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                >
                  <Button icon={<UploadOutlined />}>Browse...</Button>
                </Upload>
                <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
                  {cidFileList.length === 0 ? 'No file selected.' : cidFileList[0].name}
                </div>
              </Form.Item>
              <Form.Item
                label="Ownership Document / Authorization Letter (Optional)"
              >
                <Upload
                  beforeUpload={handleOwnershipUpload}
                  fileList={ownershipFileList}
                  onRemove={() => {
                    setOwnershipFile(null);
                    setOwnershipFileList([]);
                  }}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                >
                  <Button icon={<UploadOutlined />}>Browse...</Button>
                </Upload>
                <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
                  {ownershipFileList.length === 0 ? 'No file selected.' : ownershipFileList[0].name}
                </div>
              </Form.Item>
              <Form.Item
                label="Company Logo (Optional)"
                tooltip="Logo will be displayed in the admin dashboard when company admin logs in"
              >
                <Upload
                  beforeUpload={handleLogoUpload}
                  fileList={logoFileList}
                  onRemove={() => {
                    setLogoFile(null);
                    setLogoFileList([]);
                  }}
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  listType="picture-card"
                  maxCount={1}
                >
                  {logoFileList.length === 0 && (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload Logo</div>
                    </div>
                  )}
                </Upload>
                <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
                  Recommended: Square image (JPG, PNG, GIF, or WEBP). Larger files are compressed automatically.
                </div>
              </Form.Item>
            </Card>

            {/* Remarks Section */}
            <Card className="form-section-card remarks-card">
              <Form.Item
                label="Remarks"
                name="remarks"
              >
                <TextArea
                  rows={4}
                  placeholder="Add any additional notes..."
                  style={{ resize: 'vertical' }}
                />
              </Form.Item>
            </Card>

            {/* Action Buttons */}
            <div className="form-actions">
              <Divider />
              <Space size="large">
                <Button
                  size="large"
                  onClick={() => navigate('/superadmin/companies')}
                  style={{
                    minWidth: 120,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                  style={{
                    minWidth: 180,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  }}
                >
                  {loading ? 'Uploading documents & creating company…' : 'Register Company'}
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      </Content>
      </div>
    </Layout>
  );
};

export default AddCompany;
